import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { PopupData, RequestError, Service, Slack } from 'webapp-types';
import { equal, uniqBy } from 'webapp/utils/common';

import highlightResult from 'webapp/helpers/highlight-result';
import BarLoaderComponent from 'webapp/components/bar-loader';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import type { SelectableListItem } from 'webapp/components/selectable-list';
import SelectableListComponent from 'webapp/components/selectable-list';
import SubmitButtonComponent from 'webapp/components/submit-button';

interface ChannelOption extends LabelValuePairString {
	workspace: string;
}

export default class NotificationsSlackPopupSelectListComponent extends PopupInnerModalComponent<PopupData.Notification.Slack.SelectList> {
	@service('logger') declare loggerService: Service.Logger;
	@service('request') declare requestService: Service.Request;

	@tracked selectedChannels: ChannelOption[] = [];
	@tracked channelsList: ChannelOption[] = [];
	@tracked workspaceName = '';

	model: ChannelOption[] = [];
	searchInProperties = ['label'];

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		const selectedChannelsIds = this.selectedChannelsIds;

		this.submitInProgress = true;
		this.requestService
			.api<Slack.Channel[]>('GET', '/slack/channels')
			.then(
				(reply) => {
					const selectedChannels: ChannelOption[] = [];

					// reply = window.dbx_mock['GET /d/slack/channels'];

					if (!Array.isArray(reply)) {
						this.loggerService.logError('Slack channels not array', {
							response: JSON.stringify(reply),
						});
						return;
					}

					const model = reply.map((item) => {
						const data = {
							value: item.id,
							label: `#${item.name}`,
							workspace: item.workspace,
						};

						if (selectedChannelsIds.includes(data.value)) {
							selectedChannels.push(data);
						}

						if (!this.workspaceName) {
							this.workspaceName = item.workspace;
						}

						return data;
					});

					this.model = model;
					this.selectedChannels = selectedChannels; // since selected are shown by equal comparison, we need items from this list
					// initially put selected channels on the begginig of the list
					this.channelsList = uniqBy([...selectedChannels, ...model], 'value');
					this.submitInProgress = false;
					this.scheduleReposition();
				},
				(errorData: RequestError) => {
					this.close();
					throw errorData;
				},
			)
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	get selectedChannelsIds() {
		return this.popupData.selectedChannelsIds;
	}

	@action
	changeChannelSelection(value: SelectableListItem, isSelected: boolean) {
		const item = value as ChannelOption;
		if (isSelected) {
			if (!this.selectedChannels.includes(item)) {
				this.selectedChannels = [...this.selectedChannels, item];
			}
		} else {
			this.selectedChannels = this.selectedChannels.filter((i) => i !== item);
		}
	}

	@action
	confirm() {
		if (
			!equal(
				this.selectedChannelsIds,
				this.selectedChannels.map((item) => item.value),
			)
		) {
			this.popupData.onConfirm(
				this.selectedChannels.map((item) => {
					return {
						id: item.value,
						name: item.label.slice(1), // removing #
						workspace: item.workspace,
					};
				}),
			);
		}

		this.close();
	}
	<template>
		<div class="title">
			<CommonUiIconComponent
				@name="BrandSlack"
				@size="sm"
				@stroke="currentColor"
				@fill="none"
			/>
			Select Slack channels
			{{#if this.workspaceName}}
				from workspace "{{this.workspaceName}}"
			{{/if}}
		</div>
		<div class="content">

			{{#if this.submitInProgress}}
				<BarLoaderComponent @size="big" />
			{{else}}

				<SelectableListComponent
					@options={{this.channelsList}}
					@values={{this.selectedChannels}}
					@onItemSelectionChanged={{this.changeChannelSelection}}
					as |s|
				>
					<s.list as |list|>
						<list.row as |row|>
							{{highlightResult text=row.item.label match=row.typedFilter}}
						</list.row>
					</s.list>
				</SelectableListComponent>

			{{/if}}

			<div class="controls2">
				<div class="controls2__right">
					<SubmitButtonComponent
						@text="Cancel"
						@theme="grey-empty"
						@noDelay={{true}}
						@onTrigger={{this.close}}
					/>
					<SubmitButtonComponent
						@text="Save"
						@extraClass="save"
						@isDisabled={{this.submitInProgress}}
						@onTrigger={{this.confirm}}
					/>
				</div>
			</div>
		</div>
	</template>
}
